<script setup>
import Layout from "../Layouts/Customer.vue";
import { computed, ref, onMounted } from "vue";
import { Link, useForm, usePage } from "@inertiajs/inertia-vue3";
import { useToast } from "primevue/usetoast";
import Toast from "primevue/toast";
import Dialog from "primevue/dialog";
import Button from "primevue/button";

const props = defineProps([
    'customer',
    'menuItems',
    'flash',
    'device',
    'bookings',
    'success', // Success message from session
    'source' // Source page for back navigation
]);

// Initialize toast and dialog
const toast = useToast();
const deletingBooking = ref(false);
const confirmDialogVisible = ref(false);
const selectedBooking = ref(null);
const page = usePage();
const csrf_token = computed(() => page.props.value.csrf_token);

// Show success toast if we have a success message from server
onMounted(() => {
    if (props.success) {
        toast.add({
            severity: 'success',
            summary: 'Erfolg',
            detail: props.success,
            life: 3000,
        });
    }
    
    // Also check for success message in session storage (from redirect)
    const sessionSuccess = sessionStorage.getItem('booking_delete_success');
    if (sessionSuccess) {
        toast.add({
            severity: 'success',
            summary: 'Erfolg',
            detail: sessionSuccess,
            life: 3000,
        });
        // Clear the message so it doesn't show again on refresh
        sessionStorage.removeItem('booking_delete_success');
    }
});

const formattedBookings = computed(() => {
    if (!props.bookings || !Array.isArray(props.bookings)) {
        console.error('Bookings is not an array:', props.bookings);
        return [];
    }
    
    return props.bookings.map(booking => {
        try {
            const startDate = booking.start ? new Date(booking.start) : null;
            return {
                ...booking,
                formattedDate: startDate ? startDate.toLocaleDateString() : 'N/A',
                formattedTime: startDate ? startDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'N/A'
            };
        } catch (e) {
            console.error('Error formatting booking:', e, booking);
            return {
                ...booking,
                formattedDate: 'Error',
                formattedTime: 'Error'
            };
        }
    });
});

const showDeleteConfirmDialog = (booking) => {
    selectedBooking.value = booking;
    confirmDialogVisible.value = true;
};

const deleteBooking = () => {
    if (deletingBooking.value || !selectedBooking.value) {
        return;
    }

    deletingBooking.value = true;
    confirmDialogVisible.value = false;
    
    // Store success message in session storage to show after redirect
    sessionStorage.setItem('booking_delete_success', 'Buchung wurde erfolgreich gelöscht.');
    
    // Build the delete URL with source parameter if present
    let deleteUrl = `/bookings/${selectedBooking.value.uuid}`;
    if (props.source) {
        deleteUrl += `?source=${props.source}`;
    }
    
    // Use Inertia.js directly to handle the deletion and redirect
    useForm().delete(deleteUrl, {}, {
        // Don't prevent the full page reload
        preserveState: false,
        onError: (errors) => {
            // Clear pending success message if there's an error
            sessionStorage.removeItem('booking_delete_success');
            
            toast.add({
                severity: 'error',
                summary: 'Fehler',
                detail: 'Die Buchung konnte nicht gelöscht werden.',
                life: 3000,
            });
            console.error(errors);
            deletingBooking.value = false;
            selectedBooking.value = null;
        }
    });
};

const cancelDelete = () => {
    confirmDialogVisible.value = false;
    selectedBooking.value = null;
};

</script>

<template>
    <Layout :customer="customer" :menu-items="menuItems" :flash="flash">
        <Toast position="bottom-right" />
        
        <Dialog 
            v-model:visible="confirmDialogVisible" 
            :modal="true"
            header="Buchung löschen"
            :closable="false"
            :style="{ width: '450px' }">
            <div class="confirmation-content">
                <p class="mb-4">Sind Sie sicher, dass Sie diese Buchung löschen möchten?</p>
                <p class="mb-4" v-if="selectedBooking">
                    Datum: {{ selectedBooking.formattedDate }} um {{ selectedBooking.formattedTime }}
                </p>
            </div>
            <template #footer>
                <Button label="Nein" icon="pi pi-times" class="p-button-text" @click="cancelDelete" />
                <Button label="Ja" icon="pi pi-check" class="p-button-danger" @click="deleteBooking" :loading="deletingBooking" />
            </template>
        </Dialog>
        <div class="py-7 px-14">
            <div class="relative mb-9">
                <h1 class="text-black text-2xl font-regular">
                    Buchungen für {{ device.name }}
                </h1>
                <div class="text-gray-500 text-sm mt-2">
                    Kunde: {{ customer.first_name }} {{ customer.last_name }}
                </div>
            </div>

            <div class="bg-white shadow-md rounded-lg overflow-hidden">
                <table class="min-w-full divide-y divide-gray-200">
                    <thead class="bg-gray-50">
                        <tr>
                            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Datum
                            </th>
                            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Uhrzeit
                            </th>
                            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Provider
                            </th>
                            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Typ
                            </th>
                            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Aktionen
                            </th>
                        </tr>
                    </thead>
                    <tbody class="bg-white divide-y divide-gray-200">
                        <tr v-if="formattedBookings.length === 0">
                            <td colspan="5" class="px-6 py-4 text-center text-sm text-gray-500">
                                Keine Buchungen gefunden
                            </td>
                        </tr>
                        <tr v-for="booking in formattedBookings" :key="booking.id" class="hover:bg-gray-50">
                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {{ booking.formattedDate }}
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {{ booking.formattedTime }}
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {{ booking.provider || 'N/A' }}
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {{ booking.type || 'N/A' }}
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                <button 
                                    @click="showDeleteConfirmDialog(booking)" 
                                    class="text-red-600 hover:text-red-900 focus:outline-none"
                                    :disabled="deletingBooking"
                                >
                                    <i class="pi pi-trash"></i> Löschen
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="mt-6" v-if="source">
                <Link v-if="source === 'fitness'" 
                      :href="`/customers/${customer.uuid}/fitness`" 
                      class="text-blue-600 hover:text-blue-900">
                    ← Zurück zu Fitness
                </Link>
                <Link v-else-if="source === 'packages'" 
                      :href="`/customers/${customer.uuid}/packages`" 
                      class="text-blue-600 hover:text-blue-900">
                    ← Zurück zu Pakete
                </Link>
                <Link v-else-if="source === 'plan'" 
                      :href="`/customers/${customer.uuid}/plan`" 
                      class="text-blue-600 hover:text-blue-900">
                    ← Zurück zum Behandlungsplan
                </Link>
            </div>
        </div>
    </Layout>
</template>